/**
 * 年度金额走势统计组件（柱状和曲线混合图）
 * zxw
 * 创建时间：2025-12-10
 * 更新时间：2026-05-06
 * 更新内容：适配工作流
 */

import { useEffect, useRef, useMemo } from 'react';
import { Chart } from '@antv/g2';
import { Skeleton, theme, Empty } from 'antd';
import CNF from '../../../incs/Config.js';

const ChartILYearAmount = props => {
    const { children, id, title, subtitle, streamStatus } = props;

    const {
        token: {
            isDarkMode,
        },
    } = theme.useToken();

    const chartIdRef = useRef(`${id || 'antv-g2-id'}.${Date.now()}`);
    const chartId = chartIdRef.current;

    const chartData = useMemo(() => {
        if (Array.isArray(children)) {
            return children;
        }
        if (typeof children === 'string') {
            try {
                return JSON.parse(children);
            } catch (err) {
                return [];
            }
        }
        return [];
    }, [children]);

    useEffect(() => {
        // 如果还在加载中或者没有有效数据，就不初始化图表
        if (streamStatus === 'loading') return;

        const div = document.getElementById(chartId);
        if (!div) return;
        const width = div.offsetWidth;

        const chart = new Chart({
            container: chartId,
            autoFit: true,
            height: width > 1000 ? 600 : 400,
            theme: isDarkMode ? 'classicDark' : 'classic',
        });

        // 设置标题
        if (title) {
            chart.title({
                title: title,
                subtitle: subtitle,
            });
        }

        // 格式化数据
        chart.data(chartData);

        chart
            .interval()
            .encode('x', 'year')
            .encode('y', 'amount')
            .axis('x', {
                title: '年份',
            })
            .axis('y', {
                title: '金额',
                labelFormatter: (d) => `${d > 10000 ? (d / 10000).toFixed(2) + '万元' : d + '元'}`,
            })
            .animate('enter', {
                type: 'scaleInY',
                duration: CNF.animateDuration,
            },)
            .style('minWidth', 20)
            .style('maxWidth', 40)
            .tooltip({
                items: [(d => ({
                    name: '金额',
                    value: `${d.amount > 10000 ? (d.amount / 10000).toFixed(2) + '万元' : d.amount + '元'}`,
                }))],
            });

        chart
            .line()
            .encode('x', 'year')
            .encode('y', 'count')
            .encode('shape', 'smooth')
            .style('stroke', CNF.swatches[CNF.currentSwatche][2],)
            .style('lineWidth', 2)
            .scale('y', { independent: true })
            .axis('y', {
                position: 'right',
                grid: null,
                title: '数量',
            })
            .animate('enter', {
                type: 'pathIn',
                duration: CNF.animateDuration,
            },)
            .tooltip({
                items: [(d => ({
                    name: '数量',
                    value: `${d.count}`,
                }))],
            });

        chart.render();

        return () => {
            chart.destroy();
        }
    }, [children, streamStatus, chartId, isDarkMode]);

    // 加载时显示的骨架屏
    if (streamStatus === 'loading') {
        return <Skeleton.Image active={true} />;
    }

    // 数组为空，返回暂无数据
    if (chartData.length === 0) {
        return <Empty />;
    };

    return <div id={chartId} data-chart-type='true' />
}

export default ChartILYearAmount;